import { object, string, InferType, ValidationError, mixed } from "yup";
import { ValidateOptions } from "yup/lib/types";
import { getBrand } from "./AppBrand";
import { Brand } from "utils/models/CommonTypes";
import { getBuildNumber, getAppVersion } from "utils/helpers/AppInfo";

export const schema = object({
  BASE_URL: string().required(),
  OIDC_PROXY_BASE_URL: string().required(),
  OPC_APIM_SUB_KEY: string().required(),
  FORGET_PASSWORD_URL: string().required(),
  PASSWORD_HELP_URL: string().required(),
  SENTARA_EMPLOYER_OIDC_CLIENT_ID: string().required(),
  SENTARA_EMPLOYER_OIDC_CLIENT_SECRET: string().default(""),
  AVMED_EMPLOYER_OIDC_CLIENT_ID: string().required(),
  AVMED_EMPLOYER_OIDC_CLIENT_SECRET: string().default(""),
  LAUNCH_DARKLY_KEY: string().required(),
  BRAND: mixed<Brand>().oneOf(Object.values(Brand)).required(),
  BUILD_NUMBER: string().default(getBuildNumber()).required(),
  VERSION: string().default(getAppVersion()).required(),
});

export type AppConfig = InferType<typeof schema>;

const parseEnvVars = (): Partial<AppConfig> => {
  const prefixKey = "REACT_APP_";
  const config: Partial<AppConfig> = {};

  Object.keys(process.env)
    .filter((key) => key.startsWith(prefixKey))
    .forEach((key) => {
      const configKey = key.replace(prefixKey, "") as keyof AppConfig;
      const value = process.env[key];

      if (configKey === "BRAND") {
        return;
      }

      if (value) {
        config[configKey] = value as string;
      }
    });

  config.BRAND = getBrand();

  return config;
};

export const createAppConfig = (
  options: ValidateOptions = { abortEarly: false }
): AppConfig => {
  const config = parseEnvVars();
  return schema.validateSync(config, options) as AppConfig;
};

export const emptyConfig: AppConfig = {
  BASE_URL: "",
  OIDC_PROXY_BASE_URL: "",
  OPC_APIM_SUB_KEY: "",
  FORGET_PASSWORD_URL: "",
  PASSWORD_HELP_URL: "",
  SENTARA_EMPLOYER_OIDC_CLIENT_ID: "",
  SENTARA_EMPLOYER_OIDC_CLIENT_SECRET: "",
  AVMED_EMPLOYER_OIDC_CLIENT_ID: "",
  AVMED_EMPLOYER_OIDC_CLIENT_SECRET: "",
  LAUNCH_DARKLY_KEY: "",
  BRAND: Brand.UNKNOWN,
  BUILD_NUMBER: "",
  VERSION: "",
};

export const handleValidationError = (
  error: unknown,
  fallbackErrorMessage = "Invalid configuration: check your environment variables."
): string => {
  if (error instanceof ValidationError) {
    const formattedErrors = error.errors.map((err: string, index: number) => {
      const path = error.path;
      return `Field: ${path} | Error: ${err}`;
    });
    return `Validation failed: ${formattedErrors.join(", ")}`;
  }
  return fallbackErrorMessage;
};
